import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, PauseCircle, PlayCircle, RotateCcw, ShieldCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { api, type CallbackSettings, type Session } from "../api";
import { ErrorNotice, LoadingRows, formatTime } from "../components";

const CALLBACK_TIMEZONES = [
  ["Asia/Shanghai", "北京时间（上海）"],
  ["UTC", "协调世界时（UTC）"],
  ["Asia/Hong_Kong", "香港"],
  ["Asia/Singapore", "新加坡"],
  ["Asia/Tokyo", "东京"],
  ["Asia/Seoul", "首尔"],
  ["Asia/Kolkata", "印度"],
  ["Asia/Dubai", "迪拜"],
  ["Europe/London", "伦敦"],
  ["Europe/Berlin", "柏林"],
  ["America/New_York", "纽约"],
  ["America/Chicago", "芝加哥"],
  ["America/Denver", "丹佛"],
  ["America/Los_Angeles", "洛杉矶"],
  ["Australia/Sydney", "悉尼"],
] as const;

export function CallbackSettingsPage({ role }: { role: Session["role"] }) {
  const queryClient = useQueryClient();
  const [feedback, setFeedback] = useState<string | null>(null);
  const settings = useQuery({
    queryKey: ["callback-settings"],
    queryFn: () => api<CallbackSettings>("/admin/v1/callback-settings"),
  });
  const save = useMutation({
    mutationFn: (body: unknown) => api<CallbackSettings>("/admin/v1/callback-settings", {
      method: "PUT",
      body: JSON.stringify(body),
    }),
    onSuccess: (value) => {
      queryClient.setQueryData(["callback-settings"], value);
      setFeedback("Callback 配置已生效；新任务会快照当前默认地址，调度器将在下一轮读取新参数。");
    },
  });
  const current = settings.data;
  const canWrite = role === "ADMIN";

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!current) return;
    setFeedback(null);
    const data = new FormData(event.currentTarget);
    const retrySchedule = String(data.get("retry_schedule") ?? "")
      .split(/[,，\s]+/)
      .filter(Boolean)
      .map(Number);
    save.mutate({
      dispatch_enabled: data.get("dispatch_enabled") === "on",
      default_callback_enabled: data.get("default_callback_enabled") === "on",
      default_url: String(data.get("default_url") ?? "").trim() || null,
      signing_secret: String(data.get("signing_secret") ?? "") || null,
      max_concurrency: Number(data.get("max_concurrency")),
      request_timeout_seconds: Number(data.get("request_timeout_seconds")),
      retry_schedule_seconds: retrySchedule,
      max_retry_window_seconds: Math.round(Number(data.get("max_retry_window_hours")) * 3600),
      timezone: String(data.get("timezone") ?? "Asia/Shanghai").trim(),
      expected_version: current.version,
      reason: String(data.get("reason") ?? ""),
    });
  }

  return <div className="page page--callbacks">
    <header className="page-heading callback-heading"><div><p className="eyebrow">DELIVERY / CONTROL</p><h1>Callback 调度配置</h1><p>统一控制默认回调、签名和集群级投递节奏；已创建任务仍使用创建时快照。</p></div></header>
    {(settings.error || save.error) && <ErrorNotice error={settings.error ?? save.error} />}
    {feedback && <div className="notice notice--positive" aria-live="polite"><Check size={17} />{feedback}</div>}
    {settings.isPending || !current ? <LoadingRows columns={3} /> : <>
      <section className="callback-runtime" aria-label="Callback 当前状态">
        <div className={current.dispatch_enabled ? "callback-runtime__state callback-runtime__state--running" : "callback-runtime__state callback-runtime__state--paused"}>
          {current.dispatch_enabled ? <PlayCircle size={22} /> : <PauseCircle size={22} />}
          <div><span>调度器</span><strong>{current.dispatch_enabled ? "运行中" : "已暂停"}</strong></div>
        </div>
        <dl><div><dt>集群并发</dt><dd>{current.max_concurrency}</dd></div><div><dt>请求超时</dt><dd>{current.request_timeout_seconds}<small>s</small></dd></div><div><dt>正文时区</dt><dd className="callback-runtime__timezone">{current.timezone}</dd></div><div><dt>重试窗口</dt><dd>{formatHours(current.max_retry_window_seconds)}</dd></div></dl>
      </section>

      <form className="callback-settings-form" key={current.version} onSubmit={submit}>
        <fieldset disabled={!canWrite || save.isPending}>
          <section className="callback-settings-section">
            <div className="callback-settings-section__intro"><p className="eyebrow">DEFAULT ENDPOINT</p><h2>默认回调</h2><p>业务未传 callback_url 时使用。URL 和密钥只影响之后创建的任务。</p></div>
            <div className="callback-settings-fields">
              <label className="check callback-switch"><input name="default_callback_enabled" type="checkbox" defaultChecked={current.default_callback_enabled} /><span><strong>启用默认回调</strong><small>关闭后，无自定义 URL 的新任务不产生回调</small></span></label>
              <label className="field field--wide"><span>当前地址</span><output className="callback-current-value">{current.default_url ?? "尚未配置"}</output></label>
              <label className="field field--wide"><span>{current.default_url ? "更新 Callback URL" : "Callback URL"}</span><input name="default_url" type="url" placeholder={current.default_url ? "留空保持当前地址" : "https://business.example.com/transcode/callback"} required={current.default_callback_enabled && !current.default_url} /></label>
              <label className="field field--wide"><span>{current.signing_secret_configured ? "轮换签名 Secret" : "签名 Secret"}</span><input name="signing_secret" type="password" autoComplete="new-password" minLength={16} placeholder={current.signing_secret_configured ? "留空保持当前密钥" : "至少 16 个字符"} required={current.default_callback_enabled && !current.signing_secret_configured} /><small className="field__hint"><ShieldCheck size={13} />服务端只返回“已配置”状态，不回显密钥内容。</small></label>
            </div>
          </section>

          <section className="callback-settings-section">
            <div className="callback-settings-section__intro"><p className="eyebrow">DISPATCH POLICY</p><h2>投递策略</h2><p>并发限制作用于整个控制中心集群。正文时间按统一时区输出，暂停不会丢弃 Outbox 事件。</p></div>
            <div className="callback-settings-fields callback-settings-fields--policy">
              <label className="check callback-switch field--wide"><input name="dispatch_enabled" type="checkbox" defaultChecked={current.dispatch_enabled} /><span><strong>启用 Callback 调度</strong><small>关闭后保留待投递事件，恢复时继续处理</small></span></label>
              <label className="field"><span>集群最大并发</span><input name="max_concurrency" type="number" min="1" max="64" defaultValue={current.max_concurrency} required /></label>
              <label className="field"><span>单次请求超时（秒）</span><input name="request_timeout_seconds" type="number" min="1" max="60" defaultValue={current.request_timeout_seconds} required /></label>
              <label className="field field--wide"><span>重试间隔（秒）</span><input name="retry_schedule" defaultValue={current.retry_schedule_seconds.join(", ")} pattern="[0-9,， ]+" required /><small className="field__hint">从左到右递增，最多 12 档；用逗号分隔，例如 10, 30, 60, 300。</small></label>
              <label className="field"><span>最大重试窗口（小时）</span><input name="max_retry_window_hours" type="number" min="0.5" max="168" step="0.5" defaultValue={current.max_retry_window_seconds / 3600} required /></label>
              <TimezoneField defaultValue={current.timezone} />
              <label className="field field--wide"><span>操作原因</span><input name="reason" minLength={3} maxLength={500} placeholder="说明本次调整原因" required /></label>
            </div>
          </section>
          {canWrite ? <footer className="callback-settings-actions"><span>上次更新：{formatTime(current.updated_at)}</span><button className="button button--primary" disabled={save.isPending}><RotateCcw size={16} />{save.isPending ? "正在保存…" : "保存并生效"}</button></footer> : <footer className="callback-settings-actions"><span>当前账号为只读权限</span></footer>}
        </fieldset>
      </form>
    </>}
  </div>;
}

function formatHours(seconds: number) {
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Number((seconds / 3600).toFixed(1))}h`;
}

function TimezoneField({ defaultValue }: { defaultValue: string }) {
  const isCommon = CALLBACK_TIMEZONES.some(([value]) => value === defaultValue);
  const [selection, setSelection] = useState(isCommon ? defaultValue : "__custom");
  const [customValue, setCustomValue] = useState(isCommon ? "" : defaultValue);
  const value = selection === "__custom" ? customValue : selection;

  return <div className="field callback-timezone-field">
    <span>回调正文时区</span>
    <select value={selection} onChange={(event) => setSelection(event.target.value)}>
      {CALLBACK_TIMEZONES.map(([timezone, label]) => <option value={timezone} key={timezone}>{label} · {timezone}</option>)}
      <option value="__custom">其他 IANA 时区…</option>
    </select>
    {selection === "__custom" && <input value={customValue} onChange={(event) => setCustomValue(event.target.value)} maxLength={64} placeholder="例如 Pacific/Auckland" required />}
    <input type="hidden" name="timezone" value={value} />
    <small className="field__hint">时间格式：YYYY-MM-DD HH:mm:ss；默认北京时间，也可填写任意有效 IANA 时区。</small>
  </div>;
}
